import React from 'react';
import { NavLink, useLocation, useRouteMatch } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import { LayoutContext } from './LayoutProvider';
import NavigationIcon from './NavigationIcon';
import './NavigationListItem.css';

/**
 * This component relies a lot on arbitary values. The colors in the design
 * do not match what we're using in the codebase. We need to talk with
 * design and see if these values are something that we declare
 * in terms of primary, secondary, tertiary, etc. These values
 * map to some of the active states we have for buttons but that
 * is it.
 */

const BASE_CONSTITUENT_ROUTE_RE = new RegExp(
  `^/${AuthRoutes.CONSTITUENT.split('/')[1]}`,
);

function NavigationListItem({ exact, navRoute, itemText, name, onClick }) {
  const { isExpanded, showHover } = React.useContext(LayoutContext);
  const location = useLocation();
  const { pathname } = location;
  const match = useRouteMatch({
    path: navRoute,
    exact,
  });

  const isActive = React.useMemo(() => {
    /**
     * The constituents route is "/constituents" but individual
     * constituent routes start with "/constituent". If we have
     * a constituent route, we want to highlight the constituents
     * navigation item as active.
     */
    return (
      match ||
      (navRoute === AuthRoutes.CONSTITUENTS &&
        pathname.match(BASE_CONSTITUENT_ROUTE_RE))
    );
  }, [match, navRoute, pathname]);

  return (
    <ListItem
      button
      className={classNames(
        'NavigationListItem__button relative px-1.5 pt-1 pb-[5px] hover:bg-transparent',
        {
          'NavigationListItem__button--active font-medium [&_*]:text-primary-main':
            isActive,
          'hover:font-medium [&_*]:text-text-secondary [&_*]:hover:text-text-secondary [&_path]:text-text-disabled':
            !isActive,
        },
      )}
      focusVisibleClassName="before:opacity-100 bg-transparent"
      component={NavLink}
      data-testid="navigation-list-item"
      disableGutters
      key={name}
      to={navRoute}
      onClick={onClick}
    >
      <ListItemIcon className="h-9 min-w-[50px] justify-center p-0">
        <NavigationIcon name={name} className="w-12.5 h-9" />
      </ListItemIcon>
      <span
        title={itemText}
        className={classNames(
          'NavigationListItem__text w-[170px] basis-0 whitespace-nowrap transition-opacity duration-100',
          {
            'opacity-100': isExpanded || showHover,
            'opacity-0': !isExpanded && !showHover,
          },
        )}
      >
        {itemText}
      </span>
    </ListItem>
  );
}

NavigationListItem.propTypes = {
  exact: PropTypes.bool,
  navRoute: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  itemText: PropTypes.string,
  onClick: PropTypes.func,
};

NavigationListItem.defaultProps = {
  exact: false,
  itemText: null,
  onClick: null,
};

export default NavigationListItem;
